/**
 * Project Settings Screen
 * Dedicated page for project configuration, sidebar layout.
 *
 * Sections:
 * - Overview: name, description, language, status, min duration
 * - Channels: per-channel processing (call/chat), inherits from org
 * - Quality: Prompt Profile, Script Adherence
 * - Routing: inbound routing conditions (self-saving)
 * - Delivery: CRM delivery rules (self-saving)
 * - Danger Zone: archive / delete
 *
 * The active section is synced to ?section= so refresh and deep links work.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useRole } from '@/contexts/RoleContext';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { PageBody } from '@/components/layout/PageBody';
import { EmptyState } from '@/components/ui/empty-state';
import { DashboardSkeleton } from '@/components/ui/skeletons';
import { useProjectSettings } from './hooks/useProjectSettings';
import { ProjectSettingsLayout } from './components/ProjectSettingsLayout';
import { ProjectSettingsHeader } from './components/ProjectSettingsHeader';
import { OverviewTab } from './components/OverviewTab';
import { TeamTab } from './components/TeamTab';
import { QualityTab } from './components/QualityTab';
import { AlertChannelsTab } from './components/AlertChannelsTab';
import { RoutingTab } from './components/RoutingTab';
import { DeliveryRulesTab } from './components/DeliveryRulesTab';
import { DangerZoneTab } from './components/DangerZoneTab';
import { ChannelSettingsTab } from '@/components/settings/ChannelSettingsTab';
import { UnsavedChangesGuard } from '@/components/settings/UnsavedChangesGuard';
import {
  PROJECT_SETTINGS_SECTIONS,
  type ProjectSettingsSection,
} from './components/ProjectSettingsSidebar';

// Derived, never hand-listed: a section missing here is dropped by
// validateSearch, so its nav item navigates nowhere and the screen falls back
// to overview — the tab renders but cannot be opened.
const VALID_SECTIONS: ReadonlySet<string> = new Set(PROJECT_SETTINGS_SECTIONS);

/**
 * Sections that no longer exist, and where their old links land instead.
 *
 * `alerts` held an enable toggle and a low-score threshold that nothing on the
 * server ever read. Dropping the value would silently open Overview, so an old
 * bookmark goes to the tab that does decide alert delivery for the project.
 */
const SECTION_ALIASES: Record<string, ProjectSettingsSection> = {
  alerts: 'alert_channels',
};

interface ProjectSettingsSearch {
  section?: ProjectSettingsSection;
}

interface ProjectSettingsSectionContentProps {
  section: ProjectSettingsSection;
  state: ReturnType<typeof useProjectSettings>['state'];
  actions: ReturnType<typeof useProjectSettings>['actions'];
  projectId: string;
}

function ProjectSettingsSectionContent({
  section,
  state,
  actions,
  projectId,
}: ProjectSettingsSectionContentProps) {
  switch (section) {
    case 'overview':
      return (
        <div data-testid="project-settings-page">
          <OverviewTab state={state} actions={actions} />
        </div>
      );
    case 'team':
      return (
        <div data-testid="project-settings-page">
          <TeamTab projectId={projectId} />
        </div>
      );
    case 'quality':
      return (
        <div data-testid="project-settings-page">
          <QualityTab state={state} actions={actions} projectId={projectId} />
        </div>
      );
    case 'alert_channels':
      return (
        <div data-testid="project-settings-page">
          <AlertChannelsTab projectId={projectId} />
        </div>
      );
    case 'routing':
      return (
        <div data-testid="project-settings-page">
          <RoutingTab projectId={projectId} />
        </div>
      );
    case 'channels':
      return (
        <div data-testid="project-settings-page">
          <ChannelSettingsTab projectId={projectId} />
        </div>
      );
    case 'delivery':
      return (
        <div data-testid="project-settings-page">
          <DeliveryRulesTab projectId={projectId} />
        </div>
      );
    case 'danger_zone':
      return (
        <div data-testid="project-settings-page">
          <DangerZoneTab
            projectId={projectId}
            projectName={state.projectName}
            status={state.status}
            onArchived={() => actions.syncStatus('archived')}
          />
        </div>
      );
    default:
      return <div data-testid="project-settings-page" />;
  }
}

interface EditableProjectSettingsProps {
  projectId: string;
  urlSection?: ProjectSettingsSection;
}

function EditableProjectSettings({
  projectId,
  urlSection,
}: EditableProjectSettingsProps) {
  const navigate = useNavigate();
  const { t } = useTranslation(['projects', 'common']);
  const { isEnabled } = useFeatureFlags();

  const { state, actions, isSaving, isLoaded, isDirty, handleSave } =
    useProjectSettings(projectId);
  useDocumentTitle(
    state.projectName
      ? `${state.projectName} - ${t('common:nav.settings')}`
      : t('projects.settings')
  );

  // A deep link to a flagged-off section falls back to overview, so the URL
  // cannot reach a section the nav no longer offers.
  const activeSection =
    urlSection && (urlSection !== 'delivery' || isEnabled('enableDeliveryRules'))
      ? urlSection
      : 'overview';

  return (
    <>
      {/* Filling in a project's stop-word list takes a minute and used to
          take one navigation to lose. Team and Alert-channels keep their edits
          in their own state, invisible to `isDirty` — they report themselves
          to the shell registry the guard also reads. */}
      <UnsavedChangesGuard isDirty={isDirty} />
      <ProjectSettingsLayout
        projectName={state.projectName}
        defaultSection="overview"
        activeSection={activeSection}
        onSectionChange={(section) => {
          void navigate({
            to: '/projects/$projectId/settings',
            params: { projectId },
            search: { section },
            replace: true,
          });
        }}
        isSaving={isSaving}
        isLoaded={isLoaded}
        isDirty={isDirty}
        onSave={handleSave}
      >
        {(activeSection) => (
          <ProjectSettingsSectionContent
            section={activeSection}
            state={state}
            actions={actions}
            projectId={projectId}
          />
        )}
      </ProjectSettingsLayout>
    </>
  );
}

export function ProjectSettingsScreen() {
  const { projectId } = Route.useParams();
  const { section: urlSection } = Route.useSearch();
  const navigate = useNavigate();
  const { t } = useTranslation(['projects', 'common']);
  const { selectedOrgId, canEditProject, isLoading, isOrgSwitching } = useRole();
  useDocumentTitle(t('projects.settings'));

  if (isLoading || isOrgSwitching) {
    return (
      <PageBody>
        <ProjectSettingsHeader projectName="" />
        {/* The skeleton pads itself with p-6; PageBody already does. */}
        <div className="-mx-6 -mb-6">
          <DashboardSkeleton />
        </div>
      </PageBody>
    );
  }

  const canEdit = !!selectedOrgId && canEditProject(projectId, selectedOrgId);

  if (!canEdit) {
    return (
      <PageBody>
        <ProjectSettingsHeader projectName={null} />
        <div className="flex min-h-[60vh] items-center justify-center">
          <EmptyState
            type="no-permissions"
            title={t('projects.settingsAccessRestricted', 'Project settings restricted')}
            description={t(
              'projects.settingsAccessRestrictedDescription',
              "You don't have permission to change this project's settings."
            )}
            primaryAction={{
              label: t('projects.backToProjects', 'Back to projects'),
              onClick: () => void navigate({ to: '/projects' }),
            }}
          />
        </div>
      </PageBody>
    );
  }

  return <EditableProjectSettings projectId={projectId} urlSection={urlSection} />;
}

export const Route = createFileRoute('/projects/$projectId/settings/')({
  component: ProjectSettingsScreen,
  validateSearch: (search: Record<string, unknown>): ProjectSettingsSearch => {
    if (typeof search.section !== 'string') return {};
    if (VALID_SECTIONS.has(search.section)) {
      return { section: search.section as ProjectSettingsSection };
    }
    return { section: SECTION_ALIASES[search.section] };
  },
});
